import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { streamsAPI, projectsAPI, contributorAPI } from '../../services/api';
import { normalizeContributorRows } from '../../utils/contributorChartRows';
import { orgKeys, streamPageKeys } from '../../query/queryKeys';

/**
 * Stream detail page data: same query keys, staleTime, and enabled flags as Streams.js.
 */
export function useStreamsPageQueries({
  orgId,
  streamId,
  deploymentWindowDays,
  leadTimeWindowDays,
  churnWindowDays,
  contributorsTimeframe,
  contributorsStatus,
}: any) {
  const streamsQuery = useQuery({
    queryKey: orgKeys.streams(orgId),
    queryFn: () => streamsAPI.list(orgId),
    enabled: !!orgId,
    staleTime: 30_000,
  });
  const streams = useMemo(() => {
    const list = streamsQuery.data;
    return Array.isArray(list) ? list : [];
  }, [streamsQuery.data]);

  const projectsQuery = useQuery({
    queryKey: streamPageKeys.projects(orgId, streamId),
    queryFn: () => projectsAPI.listByStream(orgId, streamId, { stats: false }),
    enabled: !!orgId && !!streamId,
    staleTime: 30_000,
  });
  const projects = useMemo(() => {
    const list = projectsQuery.data;
    return Array.isArray(list) ? list : [];
  }, [projectsQuery.data]);

  const deploymentDoraQuery = useQuery({
    queryKey: streamPageKeys.dora(orgId, streamId, deploymentWindowDays),
    queryFn: () => streamsAPI.getDora(orgId, streamId, deploymentWindowDays),
    enabled: !!orgId && !!streamId,
    staleTime: 60_000,
  });
  const leadTimeDoraQuery = useQuery({
    queryKey: streamPageKeys.dora(orgId, streamId, leadTimeWindowDays),
    queryFn: () => streamsAPI.getDora(orgId, streamId, leadTimeWindowDays),
    enabled: !!orgId && !!streamId,
    staleTime: 60_000,
  });
  const churnQuery = useQuery({
    queryKey: streamPageKeys.churn(orgId, streamId, churnWindowDays),
    queryFn: () => streamsAPI.getChurn(orgId, streamId, churnWindowDays),
    enabled: !!orgId && !!streamId,
    staleTime: 60_000,
  });

  const streamMembersQuery = useQuery({
    queryKey: streamPageKeys.members(orgId, streamId),
    queryFn: () => streamsAPI.getMembers(orgId, streamId),
    enabled: !!orgId && !!streamId,
    staleTime: 30_000,
  });
  const streamMembers = streamMembersQuery.data ?? [];

  const streamContributorsQuery = useQuery({
    queryKey: streamPageKeys.contributorAnalytics(
      orgId,
      streamId,
      contributorsTimeframe,
      contributorsStatus
    ),
    queryFn: () =>
      contributorAPI.getByStream(orgId, streamId, {
        timeframe: contributorsTimeframe,
        status: contributorsStatus,
      }),
    enabled: !!orgId && !!streamId,
    staleTime: 60_000,
    select: (list) => normalizeContributorRows(Array.isArray(list) ? list : []),
  });
  const streamContributorRows = streamContributorsQuery.data ?? [];
  const streamContributorsLoading = streamContributorsQuery.isPending;

  const deploymentDora = deploymentDoraQuery.data?.dora ?? null;
  const deploymentDoraMeta = deploymentDoraQuery.data?.meta ?? null;
  const deploymentLoading = deploymentDoraQuery.isPending;
  const leadTimeDora = leadTimeDoraQuery.data?.dora ?? null;
  const leadTimeDoraMeta = leadTimeDoraQuery.data?.meta ?? null;
  const leadTimeLoading = leadTimeDoraQuery.isPending;
  const churnData = churnQuery.data ?? null;
  const churnLoading = churnQuery.isPending;

  return {
    streamsQuery,
    streams,
    projectsQuery,
    projects,
    streamMembersQuery,
    streamMembers,
    streamContributorsQuery,
    streamContributorRows,
    streamContributorsLoading,
    deploymentDora,
    deploymentDoraMeta,
    deploymentLoading,
    leadTimeDora,
    leadTimeDoraMeta,
    leadTimeLoading,
    churnData,
    churnLoading,
  };
}
